import { useContext } from "react"

import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useDispatch } from "react-redux"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"

import { ReasonsBreakdownChart } from "../../../components/graphs/PieChart"
import { KonanAPI } from "../../../services/KonanAPI"
import { InsightList } from "../../../types/generated/api/InsightList"
import { ReportContext } from "../Context"
import { setLabelsBreakdown } from "../Slice"
import type { InsightProps, LabelsBreakdownInsightProps } from "../interfaces"

/**
 * LabelReasonInsight Component
 *
 * Renders a breakdown visualization of reasons for label assignments in the decision-making process.
 * Fetches insight data from the API and displays it using the ReasonsBreakdownChart component.
 *
 * @param {InsightProps} insight - Insight metadata containing:
 *   - insight: Insight metadata including:
 *     - uuid: Unique identifier for the insight
 *     - status: Current status of the insight (e.g., SUCCEEDED, FAILED)
 * @returns {React.ReactElement} A Grid item containing a ReasonsBreakdownChart
 */
export const LabelReasonInsight = (props: InsightProps): React.ReactElement => {
  const { insight, reportType, isFeedbackColumn } = props
  const { id: projectId } = useParams<{ id: string }>()
  const dispatch = useDispatch()

  const { report } = useContext(ReportContext)

  const { t } = useTranslation("insights")

  // Fetch label-reasons insight data from the API
  const { data: labelReasonInsight, isLoading: isLoadingLabelReasonInsights } = useQuery<
    AxiosResponse<LabelsBreakdownInsightProps>,
    AxiosError
  >(
    ["label-reasons", projectId, report?.uuid, insight.uuid],
    () =>
      KonanAPI.fetchInsight({
        project_uuid: projectId as string,
        report_uuid: report?.uuid as string,
        insight_uuid: insight.uuid,
      }),
    {
      onSuccess: ({ data }) => {
        dispatch(setLabelsBreakdown(data))
      },
      // Only enable the query when all required parameters are available
      enabled: !!report?.uuid && !!projectId && !!insight.uuid && insight.status === InsightList.status.SUCCEEDED,
      refetchOnMount: false,
    },
  )

  const isEmpty = !labelReasonInsight?.data.data || Object.keys(labelReasonInsight?.data.data).length === 0

  return (
    <Grid item xs={12} lg={reportType === "simulation" && !isFeedbackColumn ? 8 : 12}>
      <ReasonsBreakdownChart
        title={t("LabelReasonInsight.title")}
        tooltipText={<Trans>{t("LabelReasonInsight.tooltip")}</Trans>}
        data={labelReasonInsight?.data.data}
        isLoading={isLoadingLabelReasonInsights}
        graphHeight={220}
        emptyState={
          insight.status !== InsightList.status.SUCCEEDED
            ? {
                title: `${t("label.insight")} ${t(`status.${insight.status?.toLocaleLowerCase()}`, {
                  defaultValue: insight.status,
                })?.toLocaleLowerCase()}`,
              }
            : isEmpty
              ? { title: t("LabelReasonInsight.unavailable") }
              : undefined
        }
      />
    </Grid>
  )
}
